import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type {
  ContactListItem,
  ContactListQuery,
  ContactSortField,
  ContactStatus,
} from '@neuroedge/shared';
import { Button, Card, PageState, TextField, useToast } from '../../components/ui';
import {
  DataGrid,
  dayBoundary,
  useGridState,
  type GridColumn,
  type GridState,
} from '../../components/data-grid/DataGrid';
import { useAuth } from '../../auth/AuthContext';
import { useContactCounts, useContacts } from '../../api/contacts';
import { useTags } from '../../api/tags';
import { useCreateContactSegment } from '../../api/contact-segments';
import { TagChips } from '../tags/TagChips';

const STATUSES: { value: ContactStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ARCHIVED', label: 'Archived' },
  { value: 'BLOCKED', label: 'Blocked' },
];

type Tab = 'ALL' | ContactStatus;

/** Grid state + quick search -> list query (filters are strings in the grid). */
function toListQuery(tab: Tab, search: string, g: GridState): ContactListQuery {
  const f = g.filters;
  return {
    page: g.page,
    pageSize: g.pageSize,
    sortBy: g.sortBy as ContactSortField,
    sortOrder: g.sortOrder,
    status: tab === 'ALL' ? undefined : tab,
    search: search || undefined,
    name: f.displayName?.trim() || undefined,
    company: f.companyName?.trim() || undefined,
    email: f.email?.trim() || undefined,
    phone: f.phone?.trim() || undefined,
    tagId: f.tagId || undefined,
    updatedFrom: dayBoundary(f.updatedAtFrom, false),
    updatedTo: dayBoundary(f.updatedAtTo, true),
  };
}

/**
 * Contacts on the standard `DataGrid`: status tabs with counts, a quick
 * search across name/email/company/phone, a filter under each column,
 * sorting, paging, and row selection for "Add to new campaign".
 */
export function ContactsPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canCampaign = hasPermission('campaigns.manage');

  const [tab, setTab] = useState<Tab>('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [grid, setGrid] = useGridState({ sortBy: 'updatedAt', sortOrder: 'desc' });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      if (next === search) return;
      setSearch(next);
      setGrid((g) => ({ ...g, page: 1 }));
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, search, setGrid]);

  const query = toListQuery(tab, search, grid);
  const contacts = useContacts(query);
  const counts = useContactCounts(query);
  const tags = useTags();
  const createSegment = useCreateContactSegment();

  async function saveAsSegment() {
    const name = window.prompt('Name this segment (based on the current tab, search and tag):');
    if (!name || !name.trim()) return;
    try {
      await createSegment.mutateAsync({
        name: name.trim(),
        filter: { status: query.status, tagId: query.tagId, search: query.search },
      });
      toast.success('Segment saved.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the segment.');
    }
  }

  const columns: GridColumn<ContactListItem>[] = [
    {
      key: 'displayName',
      header: 'Name',
      sortable: true,
      filter: { type: 'text' },
      render: (c) => <Link to={`/contacts/${c.id}`}>{c.displayName}</Link>,
    },
    {
      key: 'phone',
      header: 'Phone',
      filter: { type: 'text' },
      render: (c) => c.phone ?? '—',
    },
    {
      key: 'companyName',
      header: 'Company',
      sortable: true,
      filter: { type: 'text' },
      render: (c) => c.companyName ?? '—',
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      filter: { type: 'text' },
      render: (c) => c.email ?? '—',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (c) => <span className="ne-badge">{c.status}</span>,
    },
    {
      key: 'tagId',
      header: 'Tags',
      filter: {
        type: 'select',
        options: (tags.data ?? []).map((t) => ({ value: t.id, label: t.name })),
      },
      render: (c) => <TagChips tags={c.tags} empty={<span>—</span>} />,
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      sortable: true,
      filter: { type: 'dateRange' },
      render: (c) => new Date(c.updatedAt).toLocaleDateString(),
    },
  ];

  const tabs: { id: Tab; label: string; count: number | undefined }[] = [
    { id: 'ALL', label: 'All', count: counts.data?.all },
    ...STATUSES.map((s) => ({ id: s.value as Tab, label: s.label, count: counts.data?.[s.value] })),
  ];
  const filtered = Boolean(search) || Object.keys(grid.filters).length > 0 || tab !== 'ALL';

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Contacts</h1>
        <span style={{ flex: 1 }} />
        {hasPermission('contacts.create') ? (
          <>
            <Button variant="secondary" onClick={() => navigate('/contacts/import')}>
              Import
            </Button>
            <Button onClick={() => navigate('/contacts/new')}>New contact</Button>
          </>
        ) : null}
      </div>

      <Card>
        <div className="ne-toolbar">
          <div className="ne-toolbar__grow">
            <TextField
              label="Quick search"
              placeholder="Name, email, company or phone"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          {canCampaign ? (
            <Button variant="secondary" onClick={() => void saveAsSegment()}>
              Save filter as segment
            </Button>
          ) : null}
        </div>

        <div role="tablist" aria-label="Contact status" className="ne-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className="ne-tabs__tab"
              onClick={() => {
                setTab(t.id);
                setGrid({ ...grid, page: 1 });
              }}
            >
              {t.label}
              {t.count !== undefined ? <span className="ne-tabs__count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        {canCampaign && selectedIds.size > 0 ? (
          <div className="ne-toolbar" style={{ marginTop: 'var(--space-3)' }}>
            <span>{selectedIds.size} selected</span>
            <Button
              onClick={() =>
                navigate('/campaigns/new', { state: { contactIds: [...selectedIds] } })
              }
            >
              Add to new campaign
            </Button>
            <Button variant="secondary" onClick={() => setSelectedIds(new Set())}>
              Clear selection
            </Button>
          </div>
        ) : null}

        <div style={{ marginTop: 'var(--space-3)' }}>
          {contacts.isError ? (
            <PageState
              kind="error"
              message={contacts.error.message}
              onRetry={() => void contacts.refetch()}
            />
          ) : (
            <DataGrid
              columns={columns}
              rows={contacts.data?.items ?? []}
              rowKey={(c) => c.id}
              rowLabel={(c) => c.displayName}
              total={contacts.data?.meta.total ?? 0}
              state={grid}
              onStateChange={setGrid}
              loading={contacts.isFetching}
              emptyMessage={
                filtered ? 'No contacts match these filters.' : 'Create your first contact.'
              }
              selection={
                canCampaign ? { selected: selectedIds, onChange: setSelectedIds } : undefined
              }
            />
          )}
        </div>
      </Card>
    </div>
  );
}
